import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";

import Features, {sportUseProdData} from "@atg-shared/client-features";
import atgFetch from "@atg-shared/fetch";
import {getLanguage} from "@atg-shared/language";

import type {AtgRequestMultipleErrors} from "@atg-shared/fetch-types";
import type {
    KambiDetailsResponse,
    KambiDetailsState,
} from "@atg-sport-shared/sportsbook-types-kambi";
import type {GlobalSportState} from "@atg-sport-shared/sportsbook-types-root";
import type {PayloadAction} from "@reduxjs/toolkit";

const initialState: KambiDetailsState = {
    data: {
        countryCode: "",
        currencyCode: "",
        kambiBootstrapUrl: "",
        kambiEnvironment: "",
        kambiOfferingUrl: "",
        kambiSettingsApiBaseUrl: "",
        kambiWidgetApi: "",
        locale: "",
        lang: "",
        market: "",
        regulationId: "",
        clientId: 2,
        channelId: 1,
        offering: "atg",
        playerMarket: "",
    },
    loading: false,
    error: null,
    fetched: false,
};

export const FETCH_KAMBI_DETAILS = "kambiDetails/fetchData";
export const SET_KAMBI_DETAILS_FETCHED = "kambiDetails/setFetched";

export const fetchKambiDetails = createAsyncThunk(
    FETCH_KAMBI_DETAILS,
    async (_, {rejectWithValue, dispatch}) => {
        const {locale} = getLanguage();

        try {
            const response = await atgFetch<KambiDetailsResponse>(
                `/services/tokenized/kambi/api/v3/details?locale=${locale}`,
            );
            dispatch(setKambiDetailsFetched(true));

            return response.data;
        } catch (unknownError: unknown) {
            const error = unknownError as AtgRequestMultipleErrors;
            dispatch(setKambiDetailsFetched(false));
            return rejectWithValue(error);
        }
    },
);

const kambiDetailsSlice = createSlice({
    name: "kambiDetails",
    initialState,
    reducers: {
        setKambiDetailsFetched(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                fetched: action.payload,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchKambiDetails.pending, (state) => ({
                ...state,
                loading: true,
                error: null,
                fetched: false,
            }))
            .addCase(
                fetchKambiDetails.fulfilled,
                (state, action: PayloadAction<KambiDetailsResponse>) => {
                    const useProdData = Features.isEnabled(sportUseProdData);

                    return {
                        ...state,
                        data: useProdData
                            ? {
                                  ...state.data,
                                  ...action.payload,
                                  kambiOfferingUrl:
                                      "https://eu.offering-api.kambicdn.com",
                                  kambiEnvironment: "prod",
                                  market: "SE",
                                  lang: "sv_SE",
                              }
                            : {
                                  ...state.data,
                                  ...action.payload,
                                  market:
                                      // wait for backend to provide the correct type
                                      action.payload.playerMarket ||
                                      action.payload.market,
                                  lang:
                                      // wait for backend to provide the correct type
                                      action.payload.locale || action.payload.lang,
                              },
                        loading: false,
                        fetched: true,
                    };
                },
            )
            .addCase(fetchKambiDetails.rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.payload as string,
                fetched: false,
            }));
    },
});

export const {setKambiDetailsFetched} = kambiDetailsSlice.actions;

export const selectKambiDetails = (state: GlobalSportState) =>
    state.sportsbook.kambiDetails.data;

export const selectKambiDetailsFetched = (state: GlobalSportState) =>
    state.sportsbook.kambiDetails.fetched &&
    state.sportsbook.kambiDetails.data.kambiBootstrapUrl !== "" &&
    state.sportsbook.kambiDetails.data.kambiOfferingUrl !== "";

export const kambiDetailsReducer = kambiDetailsSlice.reducer;
